import {
  PushNotificationDevicesGetResponseType,
  UserCreateResponseType,
  UserNotificationPreferenceGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  ChangeEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useEffectEvent,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { TanStackTableSimple } from "@sellar-io/components/adapters";
import {
  DropdownLegacy,
  Icon,
  SpinnerLegacy,
  DropdownLegacyElement,
} from "../../../components";
import { NotificationTabs } from "../../../components/notifications/NotificationTabs";
import { Alert } from "../../../components/alert/Alert";
import { ErrorMessage } from "../../../components/errorMessage/ErrorMessage";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Card,
  CardContent,
  CardHeader,
  H4,
  IconType,
  InputCheckbox,
} from "@sellar-io/components";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { EnablePushNotificationAlert } from "../../../components/pushNotification/EnablePushNotificationAlert";
import {
  generateAndSetFirebaseMessagingToken,
  getDeviceId,
  getIsPushNotificationsDisabled,
  usePushNotificationStore,
} from "../../../components/pushNotification/PushNotificationStore";
import { TooltipContainer } from "@sellar-io/components";
import { ACCOUNT } from "../../../constants/routes";
import { useEvents, useSellarUser } from "../../../hooks";
import { usePushNotificationDeviceCreate } from "../../../hooks/api/v2/pushNotification/usePushNotificationDeviceCreate";
import { usePushNotificationDevicesList } from "../../../hooks/api/v2/pushNotification/usePushNotificationDevicesList";
import { useUser } from "../../../hooks/api/v2/user/useUser";
import { useUserNotificationPreference } from "../../../hooks/api/v2/userNotificationPreference/useUserNotificationPreference";
import { useUserNotificationPreferenceUpdate } from "../../../hooks/api/v2/userNotificationPreference/useUserNotificationPreferenceUpdate";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { FavouriteNotificationSettingsTable } from "./FavouriteNotificationSettingsTable";
import { PushNotificationDeviceDeleteModal } from "./PushNotificationDeviceDeleteModal";
import { PushNotificationDeviceRenameModal } from "./PushNotificationDeviceRenameModal";
import { getUserAccountTypes } from "../../../utils/getUserAccountTypes";

type UserNotificationPreferenceState = {
  emailConnectionRequested: boolean;
  emailConnectionAccepted: boolean;
  emailMessages: boolean;
  emailOrderRequested: boolean;
  emailOrderAccepted: boolean;
  emailOrderDispatched: boolean;
  emailOrderDelivered: boolean;
  emailOrderCancelled: boolean;
  emailOrderCommentCreated: boolean;
  emailOrderPaid: boolean;
  emailOrderPaymentExpiry: boolean;
  pushConnectionRequested: boolean;
  pushConnectionAccepted: boolean;
  pushMessages: boolean;
  pushOrderRequested: boolean;
  pushOrderAccepted: boolean;
  pushOrderDispatched: boolean;
  pushOrderDelivered: boolean;
  pushOrderCancelled: boolean;
  pushOrderCommentCreated: boolean;
  pushOrderPaid: boolean;
  pushOrderPaymentExpiry: boolean;
};

export const NotificationPreferences = (): ReactElement | null => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const { track } = useEvents();

  if (!user) throw new Error("Cannot find user");

  const navigate = useNavigate();
  const location = useLocation();

  const [message, setMessage] = useState<string>();

  const userQuery = useUser(parseInt(user.id.toString(), 10));

  const notificationPreferenceQuery = useUserNotificationPreference(
    user.UserNotificationPreferenceId,
  );

  const pushNotificationDevicesQuery = usePushNotificationDevicesList({});
  const deviceId = getDeviceId();

  const { pushNotificationsDisabled, deviceDisabled } =
    getIsPushNotificationsDisabled({ pushNotificationDevicesQuery });

  const source = location.state?.source || null;
  const trackPageView = useEffectEvent(() => {
    track("Notification Preferences Page Viewed", {
      source,
    });
  });

  useEffect(() => {
    if (source) {
      trackPageView();
    }
  }, [source]);

  return (
    <>
      <NavigationBar
        title={"Notifications"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => navigate(ACCOUNT)}
      />
      {notificationPreferenceQuery.isLoading ||
      userQuery.isLoading ||
      pushNotificationDevicesQuery.isLoading ? (
        <SpinnerLegacy />
      ) : notificationPreferenceQuery.isError ? (
        <ErrorMessage />
      ) : (
        notificationPreferenceQuery.isSuccess &&
        userQuery.isSuccess &&
        pushNotificationDevicesQuery.isSuccess && (
          <div className={"flex w-full flex-col gap-3 p-4"}>
            <Card className={twMerge("bg-white", "p-4")}>
              <H4>Notifications</H4>
              <div>
                Manage your notifications. Push notifications from Sellar are
                displayed by your approved devices.
              </div>
              {user.email !== business?.email && (
                <div className={"pt-4"}>
                  Get email notifications from Sellar and your customers sent to
                  your login email address: <strong>{user.email}</strong>.
                </div>
              )}
            </Card>

            {message && <Alert type={"information"} message={message} />}

            <Card className={twMerge("bg-white", "w-full")}>
              <CardHeader
                className={twMerge("bg-white", "flex flex-col gap-2 p-4")}
              >
                <H4>Devices</H4>
                <div>
                  Listed below are the devices on which your account has
                  approved push notifications from Sellar.
                </div>
              </CardHeader>
              <CardContent className={"p-4"}>
                <EnablePushNotificationAlert
                  title="Enable push notifications on this device?"
                  message="You need to turn on notifications for this current device to edit your push notification preferences."
                  className="mb-4 w-full"
                  deviceDisabled={deviceDisabled}
                />
                <Card className={twMerge("bg-white", "border border-zinc-300")}>
                  <div className="rounded-t-lg bg-zinc-50 px-4 py-2 font-bold">
                    Device name
                  </div>
                  {pushNotificationDevicesQuery.data.pages.map((page) =>
                    page.map((device) => (
                      <Device key={device.id} device={device} />
                    )),
                  )}
                </Card>
              </CardContent>
            </Card>

            <Card className={twMerge("bg-white", "w-full")}>
              <CardHeader
                className={twMerge("bg-white", "flex flex-col gap-2 p-4")}
              >
                <H4>Preferences</H4>
                <div>Choose which notifications you'd like to receive.</div>
              </CardHeader>
              <CardContent className={"p-4"}>
                {notificationPreferenceQuery.data && (
                  <NotificationTabs
                    showSettings={false}
                    isNotificationPopover={false}
                    tab1={
                      <UserNotificationPreferenceCardContent
                        user={user}
                        setMessage={setMessage}
                        pushNotificationsDisabled={pushNotificationsDisabled}
                        notificationPreference={
                          notificationPreferenceQuery.data
                        }
                        deviceId={deviceId}
                      />
                    }
                    tab2={
                      <FavouriteNotificationSettingsTable
                        pushNotificationsDisabled={pushNotificationsDisabled}
                      />
                    }
                  />
                )}
              </CardContent>
            </Card>
          </div>
        )
      )}
    </>
  );
};

const UserNotificationPreferenceCardContent = ({
  user,
  setMessage,
  pushNotificationsDisabled,
  notificationPreference,
  deviceId,
}: {
  user: UserCreateResponseType;
  setMessage: (message: string) => void;
  pushNotificationsDisabled: boolean;
  notificationPreference: UserNotificationPreferenceGetResponseType;
  deviceId: string;
}) => {
  const notificationPreferenceMutation = useUserNotificationPreferenceUpdate(
    user.UserNotificationPreferenceId,
  );

  const pushNotificationDeviceCreateMutation =
    usePushNotificationDeviceCreate();

  const {
    retailer: hasBuyerAccount,
    supplier: hasSupplierAccount,
    dual: hasDualAccount,
  } = getUserAccountTypes(user);

  const triggerPushPrompt = async () => {
    const notificationToken = await generateAndSetFirebaseMessagingToken();
    if (notificationToken) {
      pushNotificationDeviceCreateMutation.mutate({
        notificationToken,
        deviceId,
      });
    } else {
      usePushNotificationStore.getState().setGuideActive(true);
      return;
    }
  };
  const handleChange = async (
    preference: keyof UserNotificationPreferenceState,
    value?: boolean,
  ) => {
    const preferenceNewState = value ?? !notificationPreference[preference];
    notificationPreferenceMutation.mutate(
      {
        [preference]: preferenceNewState,
      },
      {
        onError: () =>
          setMessage("Failed to update preference. Please try again"),
      },
    );
  };
  const handlePushChange = async (
    preference: keyof UserNotificationPreferenceState,
  ) => {
    const pushBeingEnabled = !notificationPreference[preference];
    if (pushBeingEnabled) {
      await triggerPushPrompt();
    }
    handleChange(preference);
  };
  return (
    <div className="mb-4">
      <div className={"pb-4"}>
        <TanStackTableSimple
          roundedTop={true}
          columns={[
            {
              header: "Connections",
              cell: ({ row }) => <p>{row.original.text}</p>,
            },
            {
              header: "Email",
              cell: ({ row }) => (
                <PreferenceCheckbox
                  checked={notificationPreference[row.original.emailKey]}
                  onChange={() => handleChange(row.original.emailKey)}
                />
              ),
            },
            {
              header: "Push",
              cell: ({ row }) => (
                <PreferenceCheckbox
                  checked={notificationPreference[row.original.pushKey]}
                  onChange={() => handlePushChange(row.original.pushKey)}
                  disabled={pushNotificationsDisabled}
                  tooltip="Enable push notifications on this device to edit preference"
                />
              ),
            },
          ]}
          data={[
            {
              text: "Connection request received",
              emailKey:
                "emailConnectionRequested" as keyof UserNotificationPreferenceState,
              pushKey:
                "pushConnectionRequested" as keyof UserNotificationPreferenceState,
            },
            ...(hasBuyerAccount
              ? [
                  {
                    text: `Connection request accepted${hasDualAccount ? "*" : ""}`,
                    emailKey:
                      "emailConnectionAccepted" as keyof UserNotificationPreferenceState,
                    pushKey:
                      "pushConnectionAccepted" as keyof UserNotificationPreferenceState,
                  },
                ]
              : []),
          ]}
          gridClasses="grid-cols-[1fr_80px_80px]"
          clientPagination={false}
        />
      </div>

      <div className={"py-4"}>
        <TanStackTableSimple
          roundedTop={true}
          columns={[
            {
              header: "Messages",
              cell: () => <p>Direct message received</p>,
            },
            {
              header: "Email",
              cell: () => (
                <PreferenceCheckbox
                  checked={notificationPreference.emailMessages}
                  onChange={() => handleChange("emailMessages")}
                />
              ),
            },
            {
              header: "Push",
              cell: () => (
                <PreferenceCheckbox
                  checked={notificationPreference.pushMessages}
                  onChange={() => handlePushChange("pushMessages")}
                  disabled={pushNotificationsDisabled}
                  tooltip="Enable push notifications on this device to edit preference"
                />
              ),
            },
          ]}
          data={[{}]}
          gridClasses="grid-cols-[1fr_80px_80px]"
          clientPagination={false}
        />
      </div>

      <div className={"py-4"}>
        <TanStackTableSimple
          roundedTop={true}
          columns={[
            {
              header: "Order Events",
              cell: ({ row }) => <p>{row.original.text}</p>,
            },
            {
              header: "Email",
              cell: ({ row }) => (
                <PreferenceCheckbox
                  checked={notificationPreference[row.original.emailKey]}
                  onChange={() => handleChange(row.original.emailKey)}
                />
              ),
            },
            {
              header: "Push",
              cell: ({ row }) => (
                <PreferenceCheckbox
                  checked={notificationPreference[row.original.pushKey]}
                  onChange={() => handlePushChange(row.original.pushKey)}
                  disabled={pushNotificationsDisabled}
                  tooltip="Enable push notifications on this device to edit preference"
                />
              ),
            },
          ]}
          data={[
            {
              text: "Order requested",
              emailKey:
                "emailOrderRequested" as keyof UserNotificationPreferenceState,
              pushKey:
                "pushOrderRequested" as keyof UserNotificationPreferenceState,
            },
            ...(hasBuyerAccount
              ? [
                  {
                    text: `Order accepted${hasDualAccount ? "*" : ""}`,
                    emailKey:
                      "emailOrderAccepted" as keyof UserNotificationPreferenceState,
                    pushKey:
                      "pushOrderAccepted" as keyof UserNotificationPreferenceState,
                  },
                  {
                    text: `Order dispatched${hasDualAccount ? "*" : ""}`,
                    emailKey:
                      "emailOrderDispatched" as keyof UserNotificationPreferenceState,
                    pushKey:
                      "pushOrderDispatched" as keyof UserNotificationPreferenceState,
                  },
                  {
                    text: `Order delivered${hasDualAccount ? "*" : ""}`,
                    emailKey:
                      "emailOrderDelivered" as keyof UserNotificationPreferenceState,
                    pushKey:
                      "pushOrderDelivered" as keyof UserNotificationPreferenceState,
                  },
                ]
              : []),
            {
              text: "Order cancelled",
              emailKey:
                "emailOrderCancelled" as keyof UserNotificationPreferenceState,
              pushKey:
                "pushOrderCancelled" as keyof UserNotificationPreferenceState,
            },
            {
              text: "Comment added to order",
              emailKey:
                "emailOrderCommentCreated" as keyof UserNotificationPreferenceState,
              pushKey:
                "pushOrderCommentCreated" as keyof UserNotificationPreferenceState,
            },
            {
              text: "Order payment expiry (Sellar Credit only)",
              emailKey:
                "emailOrderPaymentExpiry" as keyof UserNotificationPreferenceState,
              pushKey:
                "pushOrderPaymentExpiry" as keyof UserNotificationPreferenceState,
            },
            ...(hasSupplierAccount
              ? [
                  {
                    text: `Order paid for by card payment link${hasDualAccount ? "**" : ""}`,
                    emailKey:
                      "emailOrderPaid" as keyof UserNotificationPreferenceState,
                    pushKey:
                      "pushOrderPaid" as keyof UserNotificationPreferenceState,
                  },
                ]
              : []),
          ]}
          gridClasses="grid-cols-[1fr_80px_80px]"
          clientPagination={false}
        />
      </div>
      {hasDualAccount && (
        <div className="font-bold">
          <p>*Only applicable to your buyer account(s)</p>
          <p>**Only applicable to your supplier account(s)</p>
        </div>
      )}
    </div>
  );
};

const Device = ({
  device,
}: {
  device: PushNotificationDevicesGetResponseType[number];
}) => {
  const getDeviceIcon = (deviceType: string): IconType => {
    return deviceType.includes("mobile") ? "CELLPHONE" : "DESKTOP";
  };

  const [deviceRenameModal, setDeviceRenameModal] = useState(false);
  const [deviceDeleteModal, setDeviceDeleteModal] = useState(false);

  return (
    <>
      <div
        key={device.id}
        className="flex items-center border-t border-zinc-300 px-5 py-3"
      >
        <Icon icon={getDeviceIcon(device.deviceId)} className="mr-3" />
        {device.deviceName}
        <DropdownLegacy className={"ml-auto"}>
          <DropdownLegacyElement onClick={() => setDeviceRenameModal(true)}>
            Rename device
          </DropdownLegacyElement>
          <DropdownLegacyElement
            onClick={() => setDeviceDeleteModal(true)}
            className={"text-red-400"}
          >
            Remove device permissions
          </DropdownLegacyElement>
        </DropdownLegacy>
      </div>
      <PushNotificationDeviceRenameModal
        device={device}
        onDismiss={() => setDeviceRenameModal(false)}
        active={deviceRenameModal}
      />
      <PushNotificationDeviceDeleteModal
        device={device}
        onDismiss={() => setDeviceDeleteModal(false)}
        active={deviceDeleteModal}
      />
    </>
  );
};

export const Row = ({
  text,
  textClass,
  rowClass,
  children,
}: {
  text: string;
  textClass?: string;
  rowClass?: string;
  children: ReactNode;
}) => {
  return (
    <div
      className={twMerge(
        "@container flex items-center border-b border-zinc-300 py-2 pl-4",
        rowClass,
      )}
    >
      <span
        className={twMerge(
          "mr-auto block max-w-32 @lg:max-w-64 @2xl:max-w-full",
          textClass,
        )}
      >
        {text}
      </span>
      {children}
    </div>
  );
};

export const Title = ({ text }: { text: string }) => {
  return (
    <span className="w-16 px-2 py-[6px] font-bold @sm:w-20 @xl:w-24">
      {text}
    </span>
  );
};

export const PreferenceCheckbox = ({
  checked,
  onChange,
  label,
  disabled,
  tooltip,
}: {
  checked: boolean;
  onChange?: (x?: ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  disabled?: boolean;
  tooltip?: string;
}) => {
  return (
    <TooltipContainer
      enable={!!disabled}
      placement="top"
      text={tooltip ?? "Text notifications are being discontinued"}
    >
      <div>
        <InputContainer className="w-16 border-none bg-transparent @sm:w-20 @xl:w-24">
          <InputCheckbox
            required={false}
            checked={checked}
            onChange={(e) => (onChange ? onChange(e) : null)}
            disabled={disabled}
          />
          {!!label && <InputLabel>{label}</InputLabel>}
        </InputContainer>
      </div>
    </TooltipContainer>
  );
};
